// todo social auth
// todo remember me checkbox
// todo forgot you password link

import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import type { FormikProps } from 'formik'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Link } from 'react-router-dom'

import { Typography, Button, TextField } from '@homeviews/shared-ui'
import {
  useResetPasswordMutation,
  useConfirmResetPasswordMutation,
} from '@homeviews/store'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    separator: {},
    formFooter: {},
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(-1.2),
    },
    successText: {
      marginTop: 10,
      color: '#000433 !important',
    },
    loginLink: {
      fontWeight: 700,
    },
    resetPasswordLink: {
      marginBottom: 10,
    },
  }),
)

const resetPasswordInitialValue = {
  email: '',
}

const confirmPasswordInitialValue = {
  email: '',
  password: '',
}

const resetPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Enter valid email')
    .required('Email field is required'),
})

const confirmPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Enter valid email')
    .required('Email field is required'),
  password: Yup.string().required('Password field is required'),
})

const LoginForm: FC = () => {
  const [resetPassword, { isLoading: isResettingPassword }] =
    useResetPasswordMutation()
  const [confirmPassword, { isLoading: isConfirmingPassword }] =
    useConfirmResetPasswordMutation()
  const classes = useStyles()

  const [formError, setFormError] = useState<string>('')
  const [formSuccessMsg, setFormSuccessMsg] = useState<string>('')
  const [formType, setFormType] = useState<'reset' | 'confirmReset' | ''>('')
  const [resetToken, setResetToken] = useState<string>('')
  const [initialFormState, setInitialFormState] = useState({})

  useEffect(() => {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)

    const token = urlParams.get('token')
    const email = urlParams.get('email')
    if (!token) {
      setFormType('reset')
      setInitialFormState(resetPasswordInitialValue)
    } else {
      setFormType('confirmReset')
      setResetToken(token)
      setInitialFormState({ ...confirmPasswordInitialValue, email })
    }
  }, [])

  const onSubmit = async (values) => {
    setFormSuccessMsg('')

    try {
      let payload = {}

      if (formType === 'confirmReset') {
        payload = {
          data: {
            email: values.email,
            password: values.password,
          },
          token: resetToken,
        }
        await confirmPassword(payload).unwrap()
      } else {
        payload = {
          data: {
            email: values.email,
          },
        }
        await resetPassword(payload).unwrap()
      }

      setFormSuccessMsg(
        formType === 'confirmReset'
          ? 'Password has been changed. Please login.'
          : 'Please check your email.',
      )
    } catch (err) {
      console.log(err)
      setFormError(err?.data?.msg || 'Something went wrong. Please try again.')
    } finally {
      //
    }
  }

  return (
    <div className={classes.root}>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        onSubmit={onSubmit}
        validationSchema={
          formType === 'confirmReset'
            ? confirmPasswordValidationSchema
            : resetPasswordValidationSchema
        }
      >
        {(props: FormikProps<any>) => {
          const { values, touched, errors, handleBlur, handleChange } = props

          return (
            <Form>
              <TextField
                label="Email address"
                value={values.email}
                data-id="t-email"
                id="email"
                error={errors.email && touched.email ? true : false}
                helperText={errors.email}
                onChange={(e) => {
                  handleChange(e)
                  setFormError('')
                }}
                onBlur={handleBlur}
                fullWidth
                disabled={formType === 'confirmReset'}
              />
              {formType === 'confirmReset' && (
                <TextField
                  label="Password"
                  value={values.password}
                  data-id="t-password"
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  error={errors.password && touched.password ? true : false}
                  helperText={errors.password}
                  onChange={(e) => {
                    handleChange(e)
                    setFormError('')
                  }}
                  onBlur={handleBlur}
                  fullWidth
                />
              )}
              <div className={classes.formFooter}>
                {formError && (
                  <Link to="/reset-password">
                    <Typography
                      color="primary"
                      className={classes.resetPasswordLink}
                    >
                      Not working? Start a new password reset
                    </Typography>
                  </Link>
                )}
              </div>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                customColor="primary"
                isLoading={isResettingPassword || isConfirmingPassword}
              >
                {formType === 'reset'
                  ? 'Reset Password'
                  : 'Confirm Password Reset'}
              </Button>
              {formError && (
                <Typography className="l-errorMessage">{formError}</Typography>
              )}
              {formSuccessMsg && (
                <>
                  <Typography color="primary" className={classes.successText}>
                    {formSuccessMsg}
                  </Typography>
                  {formType === 'confirmReset' && (
                    <Link to="/login">
                      <Typography color="primary" className={classes.loginLink}>
                        Login
                      </Typography>
                    </Link>
                  )}
                </>
              )}
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default LoginForm
